import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import {
  getCodeMasterList, createCodeMaster, updateCodeMaster, deleteCodeMaster,
  getCodeDetailList, createCodeDetail, updateCodeDetail, deleteCodeDetail,
} from '../../api/code';

export default function CodeManagePage() {
  const { auth } = useAuth();
  const [masters, setMasters] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [details, setDetails] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState('');

  const [creatingMaster, setCreatingMaster] = useState(false);
  const [editingMaster, setEditingMaster] = useState(null);
  const [deleteMasterTarget, setDeleteMasterTarget] = useState(null);

  const [creatingDetail, setCreatingDetail] = useState(false);
  const [editingDetail, setEditingDetail] = useState(null);
  const [deleteDetailTarget, setDeleteDetailTarget] = useState(null);

  const fetchMasters = useCallback(async () => {
    setLoadingMasters(true);
    setError('');
    try {
      const res = await getCodeMasterList({ pageNo: 1, pageSize: 100 });
      const list = res.data?.list || [];
      setMasters(list);
      if (!selectedMaster && list.length > 0) setSelectedMaster(list[0]);
    } catch (err) {
      setError(err.response?.data?.message || '마스터코드 목록을 불러오지 못했습니다.');
    } finally {
      setLoadingMasters(false);
    }
    // selectedMaster는 최초 진입 시 첫 항목을 자동 선택하기 위한 참조일 뿐이라 의도적으로 deps에서 제외한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDetails = useCallback(async (masterCode) => {
    if (!masterCode) { setDetails([]); return; }
    setLoadingDetails(true);
    try {
      const res = await getCodeDetailList(masterCode);
      setDetails(res.data || []);
    } catch {
      setDetails([]);
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  useEffect(() => { fetchMasters(); }, [fetchMasters]);
  useEffect(() => { fetchDetails(selectedMaster?.masterCode); }, [selectedMaster, fetchDetails]);

  const handleCreateMaster = async (form) => {
    try {
      await createCodeMaster({ ...form, createdBy: auth.adminName });
      setCreatingMaster(false);
      fetchMasters();
    } catch (err) {
      window.alert(err.response?.data?.message || '마스터코드 등록에 실패했습니다.');
    }
  };
  const handleUpdateMaster = async (form) => {
    try {
      await updateCodeMaster(editingMaster.masterCode, { ...form, updatedBy: auth.adminName });
      setEditingMaster(null);
      fetchMasters();
    } catch (err) {
      window.alert(err.response?.data?.message || '마스터코드 수정에 실패했습니다.');
    }
  };
  const handleDeleteMaster = async () => {
    try {
      await deleteCodeMaster(deleteMasterTarget.masterCode);
      if (selectedMaster?.masterCode === deleteMasterTarget.masterCode) setSelectedMaster(null);
      setDeleteMasterTarget(null);
      fetchMasters();
    } catch (err) {
      window.alert(err.response?.data?.message || '마스터코드 삭제에 실패했습니다.');
    }
  };

  const handleCreateDetail = async (form) => {
    try {
      await createCodeDetail({ ...form, masterCode: selectedMaster.masterCode, createdBy: auth.adminName });
      setCreatingDetail(false);
      fetchDetails(selectedMaster.masterCode);
    } catch (err) {
      window.alert(err.response?.data?.message || '상세코드 등록에 실패했습니다.');
    }
  };
  const handleUpdateDetail = async (form) => {
    try {
      await updateCodeDetail(selectedMaster.masterCode, editingDetail.detailCode, { ...form, updatedBy: auth.adminName });
      setEditingDetail(null);
      fetchDetails(selectedMaster.masterCode);
    } catch (err) {
      window.alert(err.response?.data?.message || '상세코드 수정에 실패했습니다.');
    }
  };
  const handleDeleteDetail = async () => {
    try {
      await deleteCodeDetail(selectedMaster.masterCode, deleteDetailTarget.detailCode);
      setDeleteDetailTarget(null);
      fetchDetails(selectedMaster.masterCode);
    } catch (err) {
      window.alert(err.response?.data?.message || '상세코드 삭제에 실패했습니다.');
    }
  };

  return (
    <AdminLayout title="공통코드관리" menuUrl="/admin/code">
      <div className="page-header">
        <div>
          <h1>공통코드관리</h1>
          <div className="subtitle">MASTER/DETAIL 구조의 공통코드를 관리합니다.</div>
        </div>
      </div>

      <div className="split-pane">
        <div className="card">
          <div className="flex-between" style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-border-light)' }}>
            <strong style={{ fontSize: 13 }}>마스터코드</strong>
            <button className="btn btn-ghost btn-sm" onClick={() => setCreatingMaster(true)}>+ 추가</button>
          </div>
          <div className="pane-list">
            {loadingMasters && <div className="empty-row">불러오는 중...</div>}
            {!loadingMasters && error && <div className="empty-row error-state">{error}</div>}
            {!loadingMasters && !error && masters.length === 0 && <div className="empty-row">등록된 코드가 없습니다.</div>}
            {!loadingMasters && !error && masters.map((m) => (
              <div
                key={m.masterCode}
                className={`pane-list-item${selectedMaster?.masterCode === m.masterCode ? ' selected' : ''}`}
                onClick={() => setSelectedMaster(m)}
              >
                <div>
                  <div>{m.masterName}</div>
                  <div className="text-secondary" style={{ fontSize: 11.5 }}>{m.masterCode}</div>
                </div>
                <StatusBadge value={m.useYn} />
              </div>
            ))}
          </div>
        </div>

        <div className="card card-padded">
          {!selectedMaster ? (
            <div className="empty-row">왼쪽에서 마스터코드를 선택하세요.</div>
          ) : (
            <>
              <div className="flex-between" style={{ marginBottom: 16 }}>
                <div>
                  <h2 style={{ fontSize: 15 }}>{selectedMaster.masterName} <span className="text-secondary" style={{ fontWeight: 400 }}>({selectedMaster.masterCode})</span></h2>
                  {selectedMaster.remark && <div className="text-secondary" style={{ fontSize: 12.5, marginTop: 4 }}>{selectedMaster.remark}</div>}
                </div>
                <div className="actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingMaster(selectedMaster)}>마스터 수정</button>
                  <button className="btn btn-danger btn-sm" onClick={() => setDeleteMasterTarget(selectedMaster)}>마스터 삭제</button>
                </div>
              </div>

              <div className="flex-between" style={{ marginBottom: 10 }}>
                <strong style={{ fontSize: 13 }}>상세코드</strong>
                <button className="btn btn-primary btn-sm" onClick={() => setCreatingDetail(true)}>+ 상세 추가</button>
              </div>

              <div className="table-wrap">
                <table className="data-table">
                  <thead><tr><th>순서</th><th>코드</th><th>코드명</th><th>사용여부</th><th>관리</th></tr></thead>
                  <tbody>
                    {loadingDetails && <tr><td colSpan={5} className="empty-row">불러오는 중...</td></tr>}
                    {!loadingDetails && details.length === 0 && <tr><td colSpan={5} className="empty-row">상세코드가 없습니다.</td></tr>}
                    {!loadingDetails && details.map((d) => (
                      <tr key={d.detailCode}>
                        <td>{d.sortOrder}</td>
                        <td>{d.detailCode}</td>
                        <td>{d.detailName}</td>
                        <td><StatusBadge value={d.useYn} /></td>
                        <td className="actions">
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditingDetail(d)}>수정</button>
                          <button className="btn btn-danger btn-sm" onClick={() => setDeleteDetailTarget(d)}>삭제</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {creatingMaster && <MasterFormModal onClose={() => setCreatingMaster(false)} onSave={handleCreateMaster} />}
      {editingMaster && <MasterFormModal master={editingMaster} onClose={() => setEditingMaster(null)} onSave={handleUpdateMaster} />}
      {deleteMasterTarget && (
        <ConfirmDialog title="마스터코드 삭제" message={`'${deleteMasterTarget.masterName}' 코드를 삭제하시겠습니까? 하위 상세코드도 함께 삭제될 수 있습니다.`}
          confirmLabel="삭제" danger onCancel={() => setDeleteMasterTarget(null)} onConfirm={handleDeleteMaster} />
      )}

      {creatingDetail && <DetailFormModal onClose={() => setCreatingDetail(false)} onSave={handleCreateDetail} />}
      {editingDetail && <DetailFormModal detail={editingDetail} onClose={() => setEditingDetail(null)} onSave={handleUpdateDetail} />}
      {deleteDetailTarget && (
        <ConfirmDialog title="상세코드 삭제" message={`'${deleteDetailTarget.detailName}' 상세코드를 삭제하시겠습니까?`}
          confirmLabel="삭제" danger onCancel={() => setDeleteDetailTarget(null)} onConfirm={handleDeleteDetail} />
      )}
    </AdminLayout>
  );
}

function MasterFormModal({ master, onClose, onSave }) {
  const isNew = !master;
  const [form, setForm] = useState({
    masterCode: master?.masterCode || '',
    masterName: master?.masterName || '',
    useYn: master?.useYn || 'Y',
    remark: master?.remark || '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? '마스터코드 추가' : '마스터코드 수정'} onClose={onClose}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </>}>
      <form onSubmit={submit}>
        <div className="field">
          <label>마스터코드</label>
          <input className="input" value={form.masterCode} disabled={!isNew} onChange={(e) => setForm({ ...form, masterCode: e.target.value.toUpperCase() })} required />
        </div>
        <div className="field">
          <label>코드명</label>
          <input className="input" value={form.masterName} onChange={(e) => setForm({ ...form, masterName: e.target.value })} required />
        </div>
        <div className="field">
          <label>비고</label>
          <input className="input" value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} />
        </div>
        <div className="field checkbox-row">
          <input type="checkbox" id="masterUseYn" checked={form.useYn === 'Y'} onChange={(e) => setForm({ ...form, useYn: e.target.checked ? 'Y' : 'N' })} />
          <label htmlFor="masterUseYn" style={{ margin: 0 }}>사용함</label>
        </div>
      </form>
    </Modal>
  );
}

function DetailFormModal({ detail, onClose, onSave }) {
  const isNew = !detail;
  const [form, setForm] = useState({
    detailCode: detail?.detailCode || '',
    detailName: detail?.detailName || '',
    sortOrder: detail?.sortOrder ?? 0,
    useYn: detail?.useYn || 'Y',
    remark: detail?.remark || '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave({ ...form, sortOrder: Number(form.sortOrder) }); } finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? '상세코드 추가' : '상세코드 수정'} onClose={onClose} width={360}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </>}>
      <form onSubmit={submit}>
        <div className="field">
          <label>상세코드</label>
          <input className="input" value={form.detailCode} disabled={!isNew} onChange={(e) => setForm({ ...form, detailCode: e.target.value.toUpperCase() })} required />
        </div>
        <div className="field">
          <label>코드명</label>
          <input className="input" value={form.detailName} onChange={(e) => setForm({ ...form, detailName: e.target.value })} required />
        </div>
        <div className="field">
          <label>정렬순서</label>
          <input className="input" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
        </div>
        <div className="field checkbox-row">
          <input type="checkbox" id="detailUseYn" checked={form.useYn === 'Y'} onChange={(e) => setForm({ ...form, useYn: e.target.checked ? 'Y' : 'N' })} />
          <label htmlFor="detailUseYn" style={{ margin: 0 }}>사용함</label>
        </div>
      </form>
    </Modal>
  );
}
